import { z } from 'zod'
import {
  SIDEBAR_MENU_KEYS,
  DISABLED_ROUTE_PATTERNS,
  TEMPLATE_FIELD_KEYS,
  DASHBOARD_OVERRIDE_ENTRIES,
  type DeploymentProfile
} from './deployment.types'

const capabilitySettingSchema = z
  .union([
    z.boolean(),
    z
      .object({
        enabled: z.boolean(),
        unavailableBehavior: z
          .enum(['hidden', 'disabled', 'tooltip', 'readonly', 'redirect'])
          .optional(),
        message: z.string().min(1).optional(),
        redirectTo: z.string().startsWith('/').optional()
      })
      .strict()
  ])
  .default(true)

const templateFieldSchema = z.enum(TEMPLATE_FIELD_KEYS)
const sidebarMenuKeySchema = z.enum(SIDEBAR_MENU_KEYS)
const disabledRoutePatternSchema = z.enum(DISABLED_ROUTE_PATTERNS)
const dashboardOverrideEntrySchema = z.enum(DASHBOARD_OVERRIDE_ENTRIES)
const logoUrlSchema = z.union([
  z.string().min(1),
  z
    .object({
      light: z.string().min(1),
      dark: z.string().min(1),
      collapsed: z.string().min(1).optional()
    })
    .strict()
])

const dashboardSchema = z.discriminatedUnion('mode', [
  z
    .object({
      mode: z.literal('agent_operations'),
      showQuickCreateTemplate: capabilitySettingSchema,
      showQuickCreateInstance: capabilitySettingSchema
    })
    .strict(),
  z
    .object({
      mode: z.literal('replace'),
      entry: dashboardOverrideEntrySchema
    })
    .strict()
])

export const deploymentProfileSchema = z
  .object({
    version: z.literal(1),
    profileId: z.string().min(1),
    branding: z
      .object({ productName: z.string().min(1), logoUrl: logoUrlSchema.optional() })
      .strict(),
    login: z
      .object({
        mode: z.enum(['account', 'oidc', 'saml']).default('oidc'),
        samlDomain: z.string().optional()
      })
      .strict(),
    dashboard: dashboardSchema,
    navigation: z
      .object({
        sidebar: z
          .object({
            visibleItems: z.array(sidebarMenuKeySchema).optional(),
            defaultItem: sidebarMenuKeySchema.optional()
          })
          .strict()
          .default({}),
        routes: z
          .object({
            disabledPatterns: z.array(disabledRoutePatternSchema).optional()
          })
          .strict()
          .default({})
      })
      .strict()
      .default({ sidebar: {}, routes: {} }),
    agent: z
      .object({
        templates: z
          .object({
            visible: capabilitySettingSchema,
            create: capabilitySettingSchema,
            restart: capabilitySettingSchema,
            delete: capabilitySettingSchema,
            fields: z.record(templateFieldSchema, capabilitySettingSchema).default({})
          })
          .strict(),
        instances: z
          .object({
            visible: capabilitySettingSchema,
            create: capabilitySettingSchema,
            edit: capabilitySettingSchema,
            delete: capabilitySettingSchema
          })
          .strict()
      })
      .strict(),
    channels: z
      .object({
        visible: capabilitySettingSchema,
        allowedTypes: z.array(z.string().min(1)),
        accountCreation: capabilitySettingSchema
      })
      .strict(),
    credentials: z
      .object({
        modelProvider: capabilitySettingSchema,
        objectStorage: capabilitySettingSchema,
        agentApiToken: capabilitySettingSchema,
        websocketToken: capabilitySettingSchema
      })
      .strict(),
    modules: z
      .object({
        sandbox: capabilitySettingSchema,
        mcp: capabilitySettingSchema,
        memory: capabilitySettingSchema,
        knowledgeBase: capabilitySettingSchema,
        skillGroups: capabilitySettingSchema,
        skillManagement: capabilitySettingSchema,
        skillMarketplace: capabilitySettingSchema
      })
      .strict(),
    iam: z
      .object({
        users: z
          .object({
            visible: capabilitySettingSchema,
            create: capabilitySettingSchema,
            edit: capabilitySettingSchema,
            delete: capabilitySettingSchema,
            grant: capabilitySettingSchema,
            groupAssign: capabilitySettingSchema
          })
          .strict(),
        groups: capabilitySettingSchema,
        policies: capabilitySettingSchema,
        grants: capabilitySettingSchema,
        ssoConfig: capabilitySettingSchema
      })
      .strict(),
    profile: z.object({ visible: capabilitySettingSchema }).strict(),
    theme: z
      .object({
        primaryColor: z.string().min(1).optional(),
        density: z.enum(['compact', 'comfortable']).optional()
      })
      .strict()
      .optional()
  })
  .strict()

export function parseDeploymentProfile(input: unknown): DeploymentProfile {
  return deploymentProfileSchema.parse(input) as DeploymentProfile
}
